import { useCallback, useEffect, useState } from 'react';
import {
  OnboardingDialog,
  OrgNoticeConsentDialog,
  type OnboardingSubmitValues,
  type OrgNoticeInfo,
} from '@ludolio/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';

interface OnboardingInfo {
  firstName: string;
  lastName: string;
  orgName: string | null;
  notice: OrgNoticeInfo | null;
}

/**
 * Blocks the authenticated app for first-login onboarding or when the org
 * notice has been updated and must be re-acknowledged.
 */
export function OnboardingGate() {
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const logout = useAuthStore((s) => s.logout);

  const [info, setInfo] = useState<OnboardingInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  const needsOnboarding = Boolean(user?.needsOnboarding);
  const needsOrgNoticeConsent = Boolean(user?.needsOrgNoticeConsent);
  const blocked = needsOnboarding || needsOrgNoticeConsent;

  const loadOnboardingInfo = useCallback(async () => {
    let message = 'We could not load the latest onboarding information. Check your connection and try again.';
    try {
      const res = await apiClient.getOnboardingInfo();
      if (res.success && res.data) {
        setInfo(res.data);
        setLoadError('');
        return;
      }
      message = res.error || res.message || message;
    } catch (err: any) {
      message = err?.response?.data?.message || message;
    }

    setInfo((current) => current ?? {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      orgName: null,
      notice: null,
    });
    setLoadError(message);
  }, [user?.firstName, user?.lastName]);

  useEffect(() => {
    if (blocked) {
      void loadOnboardingInfo();
    } else {
      setInfo(null);
      setLoadError('');
    }
  }, [loadOnboardingInfo, blocked]);

  if (!blocked) {
    return null;
  }
  if (!info) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[hsl(var(--color-background))]">
        <p className="text-sm text-[hsl(var(--color-muted-foreground))]">Loading account setup…</p>
      </div>
    );
  }

  const handleOnboardingSubmit = async (values: OnboardingSubmitValues) => {
    setError('');
    setSubmitting(true);
    try {
      const res = await apiClient.completeOnboarding({
        firstName: values.firstName,
        lastName: values.lastName,
        marketingConsent: values.marketingConsent,
        privacyAccepted: true,
        orgNoticeId: values.orgNoticeId,
      });
      if (res.success && res.data?.user && user) {
        setAuth({
          ...user,
          ...res.data.user,
          needsOnboarding: false,
          needsOrgNoticeConsent: Boolean(res.data.user.needsOrgNoticeConsent),
        });
      } else {
        const message = res.error || res.message || 'Failed to save your details';
        setError(message);
        await loadOnboardingInfo();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to save your details. Please try again.';
      setError(message);
      await loadOnboardingInfo();
    } finally {
      setSubmitting(false);
    }
  };

  const handleOrgNoticeSubmit = async (orgNoticeId: string) => {
    setError('');
    setSubmitting(true);
    try {
      const res = await apiClient.acceptOrgNotice(orgNoticeId);
      if (res.success && res.data?.user && user) {
        setAuth({
          ...user,
          ...res.data.user,
          needsOnboarding: false,
          needsOrgNoticeConsent: false,
        });
      } else {
        const message = res.error || res.message || 'Failed to save your acknowledgement';
        setError(message);
        await loadOnboardingInfo();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to save your acknowledgement. Please try again.';
      setError(message);
      await loadOnboardingInfo();
    } finally {
      setSubmitting(false);
    }
  };

  if (needsOrgNoticeConsent && !needsOnboarding) {
    if (!info.notice) {
      return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[hsl(var(--color-background))]">
          <div className="space-y-3 text-center">
            <p className="text-sm text-[hsl(var(--color-muted-foreground))]">
              {loadError || 'We could not load your institution\'s notice.'}
            </p>
            <button
              type="button"
              className="text-sm underline"
              onClick={() => void loadOnboardingInfo()}
            >
              Try again
            </button>
            <button
              type="button"
              className="block mx-auto text-sm text-[hsl(var(--color-muted-foreground))] underline"
              onClick={() => void logout()}
            >
              Use a different account
            </button>
          </div>
        </div>
      );
    }

    return (
      <OrgNoticeConsentDialog
        open
        orgName={info.orgName}
        notice={info.notice}
        submitting={submitting}
        error={error}
        loadError={loadError}
        onSubmit={handleOrgNoticeSubmit}
        onRetry={() => void loadOnboardingInfo()}
        onUseDifferentAccount={() => void logout()}
      />
    );
  }

  return (
    <OnboardingDialog
      open
      initialFirstName={info.firstName}
      initialLastName={info.lastName}
      orgName={info.orgName}
      notice={info.notice}
      submitting={submitting}
      error={error}
      loadError={loadError}
      onSubmit={handleOnboardingSubmit}
      onRetry={() => void loadOnboardingInfo()}
      onUseDifferentAccount={() => void logout()}
    />
  );
}
